/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the folder's category
 * of the sidebar menu
 */

// Imports
import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faLaptopFile } from "@fortawesome/free-solid-svg-icons";
import { faClock, faFolderOpen, faHandshake } from '@fortawesome/free-regular-svg-icons';

// Interfaces
import { Mission, UIChemise } from '../../interfaces/IDossiers';
import { BuroSecureSettings, PublicCabinet } from '../../interfaces/ICabinets';
import { FolderRenderableComponent } from '../../interfaces/IUtils';


export const SideBarFolderMenu = ({ expanded, ...props }) => {
    const [isExpanded, setIsExpanded] = useState(expanded || false); // Menu expand
    const [isMissionsExpanded, setIsMissionsExpanded] = useState(props.missionsExpanded || false); // Missions expand
    const [isChemisesExpanded, setIsChemisesExpanded] = useState(props.chemisesExpanded || false); // Chemises expand

    const cabinet = (JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet);
    const color = props.cabinet?.color ?? cabinet.color ?? '#E7812B';
    const backBuroSecure = JSON.parse(localStorage.getItem('buroSecure') || '{}') as BuroSecureSettings;

    useEffect(() => {
        if (props.setMissionsExpanded) props.setMissionsExpanded(isMissionsExpanded);
        if (props.setChemisesExpanded) props.setChemisesExpanded(isChemisesExpanded);
        if (props.setExpanded) props.setExpanded(isExpanded);
    }, [isMissionsExpanded, isChemisesExpanded, isExpanded, props]);

    const clearChemise = () => {
        props.chemiseHandler({} as UIChemise);
        props.missionHandler({} as Mission);
    }

    // JSX Template
    return (
        <div className={`flex flex-col items-left ${props.className || ''}`}>

            {/* Folder's home page */}
            <div className="flex flex-row items-center cursor-pointer" tabIndex={6} onClick={() => { clearChemise(); props.changeRenderedComponent(FolderRenderableComponent.FolderHome) }}>
                <FontAwesomeIcon icon={faFolderOpen} className='w-8 h-8 my-2' style={{ color }} />
                {/*<img src='/images/home.png' alt="Home" className="w-8 h-8 my-2" />*/}
                <span className={`ml-2 ${!isExpanded ? 'hidden' : ''}`}>Mon dossier</span>
                <span className={`w-4 ml-2 ${!isExpanded ? 'hidden' : ''}`}></span>
            </div>

            {/* Missions */}
            <div className={`${props.missions?.length > 0 ? isExpanded ? 'flex flex-column' : 'flex' : 'hidden'}`} tabIndex={7}>
                <div className="flex flex-row" onClick={() => { setIsExpanded(true); setIsMissionsExpanded(!isMissionsExpanded); }}>
                    <FontAwesomeIcon icon={faHandshake} className='w-8 h-8 my-2 cursor-pointer focus:outline-none' style={{ color }} />
                    {/*<img src='/images/mission.png' alt="Mission" className="w-8 h-8 my-2 cursor-pointer" />*/}
                    <div className={`items-center ${props.missions.length > 0 ? 'flex flex-grow' : 'hidden'}`}>
                        <span className={`items-center justify-between ${isExpanded ? 'flex flex-grow' : 'hidden'}`}>
                            <span className="ml-2 cursor-pointer">Missions</span>
                            <img
                                src='/images/downArrow.png'
                                alt="Down Arrow"
                                className={`w-4 h-4 ml-2 transform cursor-pointer ${isMissionsExpanded ? 'rotate-180' : ''}`} />
                        </span>
                    </div>
                </div>

                {/* Expanded missions */}
                <div className={`${isExpanded && isMissionsExpanded ? 'flex flex-row' : 'hidden'}`}>
                    <FontAwesomeIcon icon={faHandshake} className='w-8 h-8 my-2 cursor-pointer focus:outline-none invisible' style={{ color }} />
                    {/*<img src='/images/mission.png' alt="Mission" className="w-8 h-8 my-2 cursor-pointer invisible" />*/}
                    <ul className="flex-grow">
                        {props.missions.map((mission, index) => (
                            <li tabIndex={8 + index} key={'mission' + mission.MIS_REF} className="cursor-pointer flex flex-row w-[inherit]" onClick={() => { props.missionHandler(mission); props.changeRenderedComponent(FolderRenderableComponent.ChemiseDetails) }}>
                                <span className={`flex flex-grow ${props.mission === mission ? "border-l-4 pl-1" : "pl-2"}`} style={{ borderColor: color }} > {mission.MIS_CODE} - {mission.MIS_LIBELLE}</span>
                                <span className='inline-block w-4 ml-2'></span>
                            </li>
                        ))}
                    </ul>
                </div>

            </div>

            {/* Received documents / Chemises */}
            <div className={`${props.chemises?.length > 0 ? isExpanded ? 'flex flex-column' : 'flex' : 'hidden'}`} tabIndex={7 + props.missions.length}>
                <div className="flex flex-row" onClick={() => { setIsExpanded(true); setIsChemisesExpanded(!isChemisesExpanded); }}>
                    <FontAwesomeIcon icon={faFileExport} className='w-8 h-8 my-2 cursor-pointer' style={{ color }} />
                    <div className={`items-center ${props.chemises?.length > 0 ? 'flex flex-grow' : 'hidden'}`}>
                        <span className={`items-center justify-between ${isExpanded ? 'flex flex-grow' : 'hidden'}`}>
                            <span className="ml-2 cursor-pointer">Documents reçus</span>
                            <img
                                src='/images/downArrow.png'
                                alt="Down Arrow"
                                className={`w-4 h-4 ml-2 transform cursor-pointer ${isChemisesExpanded ? 'rotate-180' : ''}`} />
                        </span>
                    </div>
                </div>


                {/* Expanded chemises */}
                <div className={`${isExpanded && isChemisesExpanded ? 'flex flex-row' : 'hidden'}`}>
                    <FontAwesomeIcon icon={faFileExport} className='w-8 h-8 my-2 cursor-pointer invisible' style={{ color }} />
                    <ul className="flex-grow">
                        {props.chemises.map((chemise, index) => (
                            <li tabIndex={8 + props.missions.length + index} key={'chemise' + chemise.PAR_REF} className="cursor-pointer flex flex-row w-[inherit]" onClick={() => { props.chemiseHandler(chemise); props.changeRenderedComponent(FolderRenderableComponent.ChemiseDetails); }}>
                                <span className={`flex flex-grow ${props.chemise === chemise ? 'border-l-4 pl-1' : 'pl-2'}`} style={{ borderColor: color }}>{chemise.PAR_NOM}</span>
                                <span className='inline-block w-4 ml-2'>&nbsp;</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Sent documents */}
            {((backBuroSecure.enabled && cabinet.permissions?.isBurosecureEnabled)) ?
                //&& localStorage.getItem('eclusrRef') && ['1', '2', '4', '224', '230'].includes(localStorage.getItem('eclusrRef')!)) ? // TO REMOVE (WAITING FOR SV DEV)
                <div className="flex flex-row items-center cursor-pointer" tabIndex={10} onClick={() => { clearChemise(); props.changeRenderedComponent(FolderRenderableComponent.SentDocuments) }}>
                    <FontAwesomeIcon icon={faLaptopFile} className='w-8 h-8 my-2' style={{ color }} />
                    <span className={`ml-2 ${!isExpanded ? 'hidden' : ''}`}>Documents envoyés</span>
                    <span className={`w-4 ml-2 ${!isExpanded ? 'hidden' : ''}`}></span>
                </div> : <></>}

            {/* Actions / Events */}
            {cabinet.permissions?.isPlanningEnabled ? <div className="flex flex-row items-center cursor-pointer" tabIndex={11} onClick={() => { clearChemise(); props.changeRenderedComponent(FolderRenderableComponent.Events) }}>
                <FontAwesomeIcon icon={faClock} className='w-8 h-8 my-2' style={{ color }} />
                <span className={`ml-2 ${!isExpanded ? 'hidden' : ''}`}>Evènements</span>
                <span className={`w-4 ml-2 ${!isExpanded ? 'hidden' : ''}`}></span>
            </div> : <></>}
        </div >
    );
};


// Properties definition
SideBarFolderMenu.propTypes = {
    // Standard
    changeRenderedComponent: PropTypes.func.isRequired,
    cabinet: PropTypes.object.isRequired,
    expanded: PropTypes.bool,
    setExpanded: PropTypes.func,

    // Folder-relative properties
    mission: PropTypes.object,
    missions: PropTypes.arrayOf(PropTypes.object).isRequired,
    missionHandler: PropTypes.func.isRequired,
    missionsExpanded: PropTypes.bool,
    setMissionsExpanded: PropTypes.func,
    chemise: PropTypes.object,
    chemises: PropTypes.arrayOf(PropTypes.object).isRequired,
    chemiseHandler: PropTypes.func.isRequired,
    chemisesExpanded: PropTypes.bool,
    setChemisesExpanded: PropTypes.func,

    // Appearance
    className: PropTypes.string,
}